/**
 * Central navigation config -- single source of truth for all pages/tools.
 *
 * Consumed by: NavBar, CockpitHome, FeedbackFAB, Sidebar, SecondaryNav
 *
 * Structure (v2 — March 2026):
 *   Sidebar: Home, Vandaag, Planning, Projecten, Accounts, Productie, AI, Operations
 *   Bottom:  Feedback, Lab (gated), Admin (gated)
 */
import HomeIcon from "@mui/icons-material/Home";
import TodayIcon from "@mui/icons-material/Today";
import EventNoteIcon from "@mui/icons-material/EventNote";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PercentIcon from "@mui/icons-material/Percent";
import PersonIcon from "@mui/icons-material/Person";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import StorageIcon from "@mui/icons-material/Storage";
import BookIcon from "@mui/icons-material/Book";
import BusinessIcon from "@mui/icons-material/Business";
import GridViewIcon from "@mui/icons-material/GridView";
import DescriptionIcon from "@mui/icons-material/Description";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import BadgeIcon from "@mui/icons-material/Badge";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { FORMAAT_OMZETTEN_URL } from "./flowUrl";
import GroupsIcon from "@mui/icons-material/Groups";
import MovieIcon from "@mui/icons-material/Movie";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ChatIcon from "@mui/icons-material/Chat";
import BuildIcon from "@mui/icons-material/Build";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import VideocamIcon from "@mui/icons-material/Videocam";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StarIcon from "@mui/icons-material/Star";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CampaignIcon from "@mui/icons-material/Campaign";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import FeedbackIcon from "@mui/icons-material/Feedback";
import LanguageIcon from "@mui/icons-material/Language";
import SettingsIcon from "@mui/icons-material/Settings";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TimerIcon from "@mui/icons-material/Timer";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import BugReportIcon from "@mui/icons-material/BugReport";
import TvIcon from "@mui/icons-material/Tv";
import ScienceIcon from "@mui/icons-material/Science";
import TheatersIcon from "@mui/icons-material/Theaters";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import ExploreIcon from "@mui/icons-material/Explore";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

// -- Commonly referenced paths ------------------------------------------------
export const PATHS = {
  meetings: "/MeetingDashboard",
  materials: "/MaterialReservation",
  srtGenerator: "/SRTGenerator",
  ritmo: "/ritmo",
  srtDashboard: "/SRTDashboard",
  dashboard: "/dashboard",
  projects: "/projects",
};

// -- Work-audience mapping (mirrors RoleDashboard.jsx ROLE_MAPPING) -----------
const WORK_AUDIENCE_MAPPING = {
  PL: ["Projectleider"],
  UITVOERDER: ["Editor", "Animator", "Cameraman", "Camera Operator", "Colorist / Grader", "Geluidstechnicus", "Illustrator"],
  CREATIVE: ["Regisseur", "Creatief Strateeg", "Art Director", "Copywriter"],
  PRODUCER: ["Producer", "Productie-assistent", "Opnameleider", "Planner"],
  MANAGEMENT: ["MT", "Accountmanager", "HR", "Boekhouder"],
};

export function resolveWorkAudience(functions) {
  const arr = Array.isArray(functions)
    ? functions
    : typeof functions === "string" && functions
      ? functions.split(", ")
      : [];
  if (arr.length === 0) return null;
  for (const [audience, funcs] of Object.entries(WORK_AUDIENCE_MAPPING)) {
    if (arr.some((f) => funcs.includes(f))) return audience;
  }
  return null;
}

/**
 * NAV_ITEMS -- every navigable page/tool in the app.
 */
export const NAV_ITEMS = [
  // -- Core navigation --------------------------------------------------------
  // "/" opent sinds 25 sep (planning-gauntlet ronde 4) Vandaag (redirect in App.js). Het oude
  // widget-scherm (CockpitLite) staat onder /home: in Mijn werk (via "Alles in Mijn werk") en ⌘K.
  { path: "/home", label: "Mijn widgets", icon: HomeIcon, category: "Mijn Werk" },
  { path: "/vandaag", label: "Vandaag", icon: TodayIcon, quickNav: { roles: ["*"] } },
  { path: "/dashboard", label: "Mijn Dashboard", icon: PersonIcon, featured: true, quickNav: { roles: ["*"] }, audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },

  // -- Planning ---------------------------------------------------------------
  { path: "/slim-uren", label: "Slim Uren", icon: TimerIcon, category: "Planning", quickNav: { roles: ["*"] }, audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },
  { path: "/mail-assistent", label: "Frits (mail-assistent)", icon: FeedbackIcon, category: "Planning", audiences: ["PL", "MANAGEMENT"] },
  { path: "/ai-mail", label: "AI-mail", icon: FeedbackIcon, category: "Planning", audiences: ["MANAGEMENT"] },
  { path: "/dagstart", label: "Dagstart", icon: CalendarViewWeekIcon, category: "Planning", featured: true, audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER"] },
  { path: "/planning/overzicht-per-project", label: "Planningsgrid per project", icon: ViewWeekIcon, category: "Planning", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/planning/weekoverzicht-pl", label: "Weekoverzicht per PL", icon: CalendarViewWeekIcon, category: "Planning", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/weekplan", label: "Weekplan", icon: CalendarViewWeekIcon, category: "Planning", audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },
  { path: "/overdracht", label: "Overdracht (PL)", icon: SwapHorizIcon, category: "Planning", audiences: ["PL", "MANAGEMENT"] },
  { path: "/leveranciersbeoordelingen", label: "Leveranciersbeoordelingen", icon: StarIcon, category: "Planning", audiences: ["PL", "MANAGEMENT"] },
  // HR & leveranciers (26 sep 2026, gap-analyse #7–#9). Galerij voor iedereen; de rest achter hr_leveranciers.
  { path: "/medewerkers", label: "Medewerkers", icon: GroupsIcon, category: "Mijn Werk" },
  // Bestanden delen (okt 2026): intern een bestand naar collega's sturen — Voor mij / Verstuurd, 90 dagen
  { path: "/bestanden", label: "Bestanden delen", icon: SendIcon, category: "Mijn Werk", feature: "bestanden" },
  { path: "/task-board", label: "Weekbord", icon: ViewWeekIcon, category: "Planning", quickNav: { roles: ["*"] }, audiences: ["PL", "UITVOERDER", "CREATIVE"] },
  { path: "/capacity", label: "Capaciteit", icon: GridViewIcon, category: "Planning", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/planner-todo", label: "Rooster", icon: AssignmentIndIcon, category: "Planning", feature: "planner_todo", quickNav: { roles: ["admin", "management", "projectleider"] }, audiences: ["PL", "PRODUCER"] },
  { path: "/planning/inplannen", label: "Nog in te plannen", icon: AssignmentIndIcon, category: "Planning", feature: "planner_todo", audiences: ["PL", "PRODUCER"] },
  { path: "/slimme-toewijzing", label: "Slimme toewijzing", icon: AssignmentIndIcon, category: "Planning", feature: "planner_todo", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/draaidag-planner", label: "Draaidag-planner", icon: AssignmentIndIcon, category: "Planning", feature: "planner_todo", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/MaterialReservation", label: "Materialen reserveren", icon: BookIcon, category: "Planning", featured: true, audiences: ["PRODUCER"] },
  // Draaidagen-overzicht: sinds 26 sep een planningstab (stond eerder alleen op de hub, zonder titel/⌘K)
  { path: "/draaidagen", label: "Draaidagen", icon: VideocamIcon, category: "Planning", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/planning/corvee", label: "Corvee", icon: CleaningServicesIcon, category: "Planning", audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },

  // -- Projecten (no category — direct sidebar entry) -------------------------
  { path: "/projects", label: "Projecten", icon: FolderOpenIcon, featured: true, quickNav: { roles: ["*"] }, audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  // Notes: supersnel een notitie ("iemand belt") — koppelen aan klant/project kan later.
  { path: "/notes", label: "Notities", icon: StickyNote2Icon, featured: true, quickNav: { roles: ["*"] }, audiences: ["PL", "PRODUCER", "MANAGEMENT"] },

  // -- Accounts (hub with commercial/content tools) ---------------------------
  { path: "/marketing", label: "Marketing OS", icon: CampaignIcon, category: "Accounts", audiences: ["CREATIVE", "MANAGEMENT"] },
  { path: "/aanvragen-portaal", label: "Klantaanvragen", icon: InboxIcon, category: "Accounts", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/contentkalender", label: "Contentkalender", icon: EventNoteIcon, category: "Accounts", audiences: ["CREATIVE", "PL", "MANAGEMENT"] },
  { path: "/website-preview", label: "Website Preview", icon: LanguageIcon, category: "Accounts", feature: "website_feedback" },
  { path: "/sharepoint-analyse", label: "SharePoint opslag", icon: StorageIcon, category: "Operations", feature: "sharepoint_analyse" },

  // -- Productie (new hub) ----------------------------------------------------
  { path: "/callsheets", label: "Callsheets", icon: DescriptionIcon, category: "Productie", audiences: ["PRODUCER"] },
  { path: "/script-breakdown", label: "Script Breakdown", icon: MovieFilterIcon, category: "Productie", audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/productie-breakdown", label: "Productie Breakdown", shortLabel: "Breakdown", icon: FactCheckIcon, category: "Productie", feature: "productie_breakdown", audiences: ["PRODUCER", "PL", "UITVOERDER"] },
  { path: "/ai-voortgang", label: "AI Voortgang", shortLabel: "Voortgang", icon: FactCheckIcon, category: "Productie", feature: "ai_voortgang", audiences: ["PL", "UITVOERDER", "CREATIVE"] },
  { path: "/video-qa", label: "Video Edit QA", shortLabel: "Edit QA", icon: BugReportIcon, category: "Productie", audiences: ["UITVOERDER", "PL"] },
  { path: "/mxf-check", label: "MXF Check", shortLabel: "MXF", icon: FactCheckIcon, category: "Productie", feature: "mxf_check", audiences: ["UITVOERDER", "PRODUCER"] },
  { path: "/SRTGenerator", label: "SRT Generator", icon: SubtitlesIcon, category: "Productie", audiences: ["UITVOERDER"] },
  { path: "/ritmo", label: "Ondertitel Studio", shortLabel: "Ondertitels", icon: MovieFilterIcon, category: "Productie", audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },
  { path: "/SRTDashboard", label: "SRT Beheer", icon: DashboardIcon, category: "Productie", audiences: ["UITVOERDER"] },
  { path: "/video-downloader", label: "Video Downloader", shortLabel: "Downloader", icon: DownloadIcon, category: "Productie", audiences: ["UITVOERDER"] },
  { path: "/name-title-generator", label: "Titel Generator", shortLabel: "Titels", icon: BadgeIcon, category: "Productie", audiences: ["UITVOERDER", "CREATIVE"] },
  // Woont in Flow (Full Frame-account): opent in een nieuw tabblad; /formaat-omzetten stuurt door.
  { path: "/formaat-omzetten", label: "Formaat omzetten", shortLabel: "Formaat", icon: AspectRatioIcon, category: "Productie", externalUrl: FORMAAT_OMZETTEN_URL, audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/pptx-video-replacer", label: "PowerPoint Video Replacer", shortLabel: "PPTX Replacer", icon: SlideshowIcon, category: "Productie", audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/narrowcasting", label: "Narrowcasting", icon: TvIcon, category: "Productie", audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/pdf-combiner", label: "PDF Combiner", icon: DescriptionIcon, category: "Productie", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/SRTomzettenFCP", label: "SRT naar FCP", icon: SubtitlesIcon, hidden: true },

  // -- AI ---------------------------------------------------------------------
  { path: "/ai-chat", label: "AI chat project", icon: ChatIcon, category: "AI" },
  { path: "/gpt-maker", label: "GPT Bouwer", icon: BuildIcon, category: "AI", audiences: ["CREATIVE"] },
  { path: "/explainer-script", label: "Explainer Studio", shortLabel: "Explainer", icon: AutoFixHighIcon, category: "AI", audiences: ["CREATIVE"] },
  { path: "/ai-beelden", label: "AI-beelden (Higgsfield)", shortLabel: "AI-beelden", icon: AutoFixHighIcon, category: "AI", audiences: ["CREATIVE", "PL"] },
  // Geen audiences: regisseurs én adviseurs moeten hier bij kunnen (Joris, 10 sep 2026).
  { path: "/scriptstijlen", label: "Scriptstijlen", icon: EditNoteIcon, category: "AI" },
  { path: "/animatie-leren", label: "Animatie leren", icon: EditNoteIcon, category: "AI" },
  { path: "/animatie-jobs", label: "Animatie-jobs", icon: EditNoteIcon, category: "AI" },
  { path: "/MeetingDashboard", label: "Meetings", shortLabel: "Meetings", icon: GroupsIcon, category: "Mijn Werk", audiences: ["PL", "CREATIVE"] },
  { path: "/video-search", label: "Videotheek", icon: VideoLibraryIcon, category: "AI", audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/inspiration", label: "Inspiratie", icon: MovieIcon, category: "AI", audiences: ["CREATIVE"] },
  { path: "/effie-guide", label: "Effie Awards Gids", shortLabel: "Effie Gids", icon: EmojiEventsIcon, category: "AI", audiences: ["CREATIVE", "PL"] },
  { path: "/avatar-chat", label: "Avatar Chat", icon: VideocamIcon, category: "AI", audiences: ["CREATIVE"] },

  // -- Operations (management, gated) -----------------------------------------
  { path: "/sales-dashboard", label: "Sales Dashboard", shortLabel: "Sales", icon: TrendingUpIcon, category: "Operations", feature: "sales_dashboard", quickNav: { roles: ["admin", "management"] }, audiences: ["MANAGEMENT"] },
  { path: "/sales/prospects", label: "Sales Prospects", shortLabel: "Prospects", icon: HandshakeIcon, category: "Operations", feature: "sales_prospects", audiences: ["MANAGEMENT"] },
  { path: "/offertes", label: "Offerte Generator", shortLabel: "Offertes", icon: HandshakeIcon, category: "Operations", feature: "offertes", audiences: ["MANAGEMENT", "PL"] },
  { path: "/richtprijzen", label: "Richtprijzen klant-assistent", shortLabel: "Richtprijzen", icon: HandshakeIcon, category: "Operations", feature: "offertes", audiences: ["MANAGEMENT", "PL"] },
  { path: "/achmea-export", label: "Achmea factuur-export", shortLabel: "Achmea-export", icon: HandshakeIcon, category: "Operations", feature: "offertes", audiences: ["MANAGEMENT", "PL"] },
  { path: "/revenue-forecast", label: "Revenue Forecast", shortLabel: "Forecast", icon: ShowChartIcon, category: "Operations", feature: "revenue_forecast", audiences: ["MANAGEMENT"] },
  // Budget per account (27 sep 2026): targets volgend jaar + export naar het Revenue-blad — eigen recht "budget" (admin, management)
  { path: "/budget", label: "Budget", icon: ShowChartIcon, category: "Operations", feature: "budget", audiences: ["MANAGEMENT"] },
  { path: "/opdracht-mix", label: "Opdracht-mix", shortLabel: "Mix", icon: DashboardIcon, category: "Operations", feature: "kpi_dashboard", audiences: ["MANAGEMENT"] },
  { path: "/ohw-check", label: "OHW Check", shortLabel: "OHW", icon: FactCheckIcon, category: "Operations", feature: "kpi_dashboard", quickNav: { roles: ["admin", "management"] }, audiences: ["MANAGEMENT"] },
  { path: "/uren-check", label: "Uren Controle", shortLabel: "Uren", icon: ScheduleIcon, category: "Operations", feature: "kpi_dashboard", quickNav: { roles: ["admin", "management"] }, audiences: ["MANAGEMENT"] },
  { path: "/enquete-dashboard", label: "Klanttevredenheid", icon: StarIcon, category: "Operations", feature: "enquete_dashboard", audiences: ["MANAGEMENT"] },
  { path: "/strategy", label: "Strategie", icon: ExploreIcon, feature: "strategy", audiences: ["MANAGEMENT"] },
  { path: "/hr-dashboard", label: "HR-dashboard", icon: GroupsIcon, category: "Operations", feature: "hr_leveranciers", audiences: ["MANAGEMENT"] },
  // Leveranciers (23 okt): het overzicht is er ook voor PL's (recht leveranciers); de HR-tabbladen vragen hr_leveranciers.
  { path: "/leveranciers", label: "Leveranciers", icon: HandshakeIcon, category: "Operations", feature: "leveranciers", audiences: ["PL", "MANAGEMENT"] },
  // Sleuteloverzicht (26 sep 2026, gap-analyse #6): keytags van het pand — eigen recht "sleutels" (admin, management; office via override)
  { path: "/sleutels", label: "Sleuteloverzicht", shortLabel: "Sleutels", icon: VpnKeyIcon, category: "Operations", feature: "sleutels", audiences: ["MANAGEMENT"] },
  // Werving (ATS, sprint 1 — 4 okt 2026): vacatures, sollicitaties uit ikwilwerken@, blind scoren — recht "werving" (MT; HR/teamleads via override)
  { path: "/werving", label: "Werving", icon: PersonSearchIcon, category: "Operations", feature: "werving", audiences: ["MANAGEMENT"] },

  // -- User / Admin -----------------------------------------------------------
  { path: "/mijn-profiel", label: "Mijn profiel", icon: PersonIcon, inUserMenu: true },
  { path: "/my-feedback", label: "Mijn Feedback", icon: FeedbackIcon, inUserMenu: true },
  // Wijzigingen testen (okt 2026): per pagina wat er veranderd is, test of live, teststatus + feedback — recht "wijzigingen" (admin, management)
  { path: "/wijzigingen", label: "Wijzigingen testen", icon: FactCheckIcon, feature: "wijzigingen", inUserMenu: true },
  { path: "/admin/settings", label: "Admin Instellingen", icon: SettingsIcon, feature: "admin_settings", inUserMenu: true },
  { path: "/joris-dashboard", label: "Werkdruk PL's & team", icon: DashboardIcon, category: "Operations", feature: "kpi_dashboard", quickNav: { roles: ["admin", "management"] }, audiences: ["MANAGEMENT"] },
  { path: "/declarabiliteit", label: "Declarabiliteit", icon: PercentIcon, category: "Operations", feature: "kpi_dashboard", audiences: ["MANAGEMENT"] },
  { path: "/declarabiliteit/persoon/:email", label: "Declarabiliteit per persoon", icon: PercentIcon, category: "Operations", feature: "kpi_dashboard", hidden: true },
  { path: "/sync-check", label: "Sync-check (Airtable ↔ Gripp)", icon: SyncProblemIcon, inUserMenu: true, emailAllowlist: ["joris@fullframe.nl"] },
  { path: "/lab", label: "Rick's Laboratorium", shortLabel: "Lab", icon: ScienceIcon, feature: "lab" },

  // -- Hub pages --------------------------------------------------------------
  { path: "/ai-hub", label: "AI", icon: AutoFixHighIcon },
  { path: "/accounts", label: "Accounts", icon: BusinessIcon },
  { path: "/planning", label: "Planning", icon: ViewWeekIcon },
  { path: "/productie", label: "Productie", icon: TheatersIcon },
  { path: "/operations", label: "Operations", icon: ShowChartIcon },

  // -- Dynamic sub-routes (hidden, for title/icon resolution) -----------------
  { path: "/accounts/:accountId/workspace", label: "Account Workspace", icon: BusinessIcon, hidden: true },
  { path: "/projects/:id", label: "Project Details", icon: FolderOpenIcon, hidden: true },
  { path: "/callsheets/:id", label: "Callsheet Bewerken", icon: DescriptionIcon, hidden: true },
  { path: "/MeetingDashboard/:id", label: "Meeting", icon: GroupsIcon, hidden: true },
  // Overlegmodule (20 sep 2026): Mijn week, overlegpagina, actieregister — zichtbaarheid per overleg op de server
  { path: "/overleg", label: "Overleggen", shortLabel: "Overleg", icon: EventNoteIcon, category: "Mijn Werk", feature: "overleg", featured: true, quickNav: { roles: ["*"] }, audiences: ["PL", "PRODUCER", "MANAGEMENT", "CREATIVE"] },
  { path: "/overleg/acties", label: "Actieregister", icon: EventNoteIcon, category: "Mijn Werk", feature: "overleg" },
  // Verbeterregister (26 sep 2026, gap-analyse #5): Airtable blijft de bron; naast het actieregister
  // omdat verbeterpunten in de overleggen worden besproken en iedereen er een mag indienen.
  { path: "/verbeterregister", label: "Verbeterregister", icon: FactCheckIcon, category: "Mijn Werk", feature: "overleg" },
  { path: "/overleg/sessie/:id", label: "Overleg", icon: EventNoteIcon, hidden: true },
];

// -- Derived lookups ----------------------------------------------------------

const _pathMap = new Map();
NAV_ITEMS.forEach((item) => {
  if (!item.path.includes(":")) {
    _pathMap.set(item.path, item);
  }
});

const _dynamicItems = NAV_ITEMS.filter((item) => item.path.includes(":")).map(
  (item) => ({
    ...item,
    _regex: new RegExp("^" + item.path.replace(/:[^/]+/g, "[^/]+") + "$"),
  })
);

function _findItem(pathname) {
  const exact = _pathMap.get(pathname);
  if (exact) return exact;
  for (const item of _dynamicItems) {
    if (item._regex.test(pathname)) return item;
  }
  for (const [path, item] of _pathMap) {
    if (path !== "/" && pathname.startsWith(path + "/")) return item;
  }
  return null;
}

/** Naam van de interne tool (besluit Joris 26 sep 2026). De bedrijfsnaam blijft Full Frame. */
export const APP_NAAM = "Toolframe";

/** Get page title for a pathname. */
export function getPageTitle(pathname) {
  const item = _findItem(pathname);
  return item ? item.label : APP_NAAM;
}

/**
 * Browsertitel: "<Pagina> · Toolframe", of alleen "Toolframe" als het pad geen eigen naam
 * heeft. Op test ervoor "🧪 TEST · " — met tien tabs open is de tab de enige plek waar je
 * ziet dat je op test zit (verzoek 20 aug).
 */
export function browserTitel(pathname, { test = false } = {}) {
  const item = _findItem(pathname);
  const pagina = item?.label;
  const basis = pagina && pagina !== APP_NAAM ? `${pagina} · ${APP_NAAM}` : APP_NAAM;
  return test ? `🧪 TEST · ${basis}` : basis;
}

/** Get page icon component class for a pathname. Caller renders: <Icon /> */
export function getPageIcon(pathname) {
  const item = _findItem(pathname);
  return item?.icon || null;
}

/** Resolve a tool name for any pathname (static + dynamic). */
export function resolveToolName(pathname) {
  const item = _findItem(pathname);
  return item ? item.label : null;
}

/** Get quickNav items for a given RBAC role, filtered by permissions. */
export function getQuickNav(role = "team", permissions = {}) {
  return NAV_ITEMS.filter((item) => {
    if (!item.quickNav) return false;
    const roles = item.quickNav.roles;
    if (!roles.includes("*") && !roles.includes(role)) return false;
    if (item.feature && !permissions[item.feature]?.view) return false;
    return true;
  }).map((item) => ({
    path: item.path,
    label: item.shortLabel || item.label,
    Icon: item.icon,
  }));
}

/** Get featured tools for CockpitHome. */
export function getFeaturedTools() {
  return NAV_ITEMS.filter((item) => item.featured && !item.hidden).map(
    (item) => ({
      to: item.path,
      title: item.shortLabel || item.label,
      Icon: item.icon,
    })
  );
}

/** Get tool categories for CockpitHome grid. */
export function getToolCategories(workAudience = null) {
  const catMap = new Map();
  const catOrder = [];

  NAV_ITEMS.forEach((item) => {
    if (!item.category || item.hidden) return;
    if (!catMap.has(item.category)) {
      catMap.set(item.category, { items: [], audienceScore: 0 });
      catOrder.push(item.category);
    }
    const cat = catMap.get(item.category);
    cat.items.push({
      to: item.path,
      title: item.shortLabel || item.label,
      Icon: item.icon,
      feature: item.feature || null,
    });
    if (workAudience && item.audiences?.includes(workAudience)) {
      cat.audienceScore += 1;
    }
  });

  const sorted = workAudience
    ? [...catOrder].sort((a, b) => {
        const scoreA = catMap.get(a).audienceScore;
        const scoreB = catMap.get(b).audienceScore;
        if (scoreB !== scoreA) return scoreB - scoreA;
        return catOrder.indexOf(a) - catOrder.indexOf(b);
      })
    : catOrder;

  return sorted.map((title) => {
    const { items } = catMap.get(title);
    const allGated = items.every((it) => it.feature);
    return {
      title,
      feature: allGated ? items[0].feature : null,
      items,
    };
  });
}

/** Get TOOL_NAMES mapping for FeedbackFAB. */
/** Paden + namen voor de toolkeuzelijst van de feedbackknop. Verborgen pagina's zonder
 *  parameter horen erbij: Home staat niet in het menu, maar je wilt er wel feedback over
 *  kunnen geven (tool-feedback #19/#27/#30). */
export function getToolNames() {
  const map = {};
  NAV_ITEMS.forEach((item) => {
    if (item.path.includes(":")) return;
    map[item.path] = item.label;
  });
  return map;
}

/** Get user menu items (avatar dropdown). */
export function getUserMenuItems(permissions = {}, userEmail = "") {
  const emailLower = (userEmail || "").toLowerCase();
  return NAV_ITEMS.filter((item) => {
    if (!item.inUserMenu) return false;
    if (item.feature && !permissions[item.feature]?.view) return false;
    if (item.emailAllowlist && !item.emailAllowlist.includes(emailLower)) return false;
    return true;
  }).map((item) => ({
    path: item.path,
    label: item.label,
    Icon: item.icon,
  }));
}

const DEFAULT_ZICHTBAAR = { showBeta: true, showPlanned: false };

/**
 * Hoofdmenu (menu-herziening 25 sep 2026, ronde 2 — zie planning/MENU-VOORSTEL.md):
 * één menukolom. Het actieve hoofditem klapt zijn sectie open (accordeon, SidebarNav);
 * er is geen aparte tweede kolom meer.
 *   - `path` = het STARTSCHERM van de sectie (sectieStart), niet de hub. De hub blijft
 *     bereikbaar via "Alle …" onderaan de uitgeklapte sectie.
 *   - Ronde 4: geen Home-item meer ("/" = Vandaag = start van Mijn werk). Submenu
 *     (accordeon) alleen bij secties met `submenu: true` (Mijn werk, Accounts); Planning
 *     heeft tabs, Productie/AI/Operations starten op hun hub die alles al toont.
 *   - `groep`: 'dagelijks' (Mijn werk, Planning, Projecten — zonder kopje),
 *     'organisatie' (Accounts, Productie, AI) en 'sturing' (Operations, alleen management).
 *     SidebarNav zet boven elke volgende groep een klein stil kopje (MENU_GROEPEN).
 * Wat eerder uit het hoofdmenu ging staat nog steeds in een menu én in ⌘K:
 *   Privé-to-do's, Notities → sectie Mijn werk
 *   Feedback, Admin, Sync-check, BETA/PLANNED-schakelaars, versie → profielmenu (AppShell)
 *   Lab → onderaan de zijbalk (alleen met lab-recht)
 * `activePaths`: het hoofditem licht ook op als je op een scherm uit die sectie staat.
 */
export function getSidebarItems(permissions = {}, _userEmail = "", visibility = DEFAULT_ZICHTBAAR) {
  const sectiePaden = (key) => SECONDARY_NAV_SECTIONS[key]?.paths || [];
  const sectieItem = (key, label, Icon, groep, extra = []) => (sectieZichtbaar(key, permissions) ? [{
    path: sectieStart(key, permissions, visibility),
    label,
    Icon,
    section: key,
    submenu: !!SECONDARY_NAV_SECTIONS[key]?.submenu,
    groep,
    activePaths: [...sectiePaden(key), ...extra],
  }] : []);
  return {
    primary: [
      // "/" = Vandaag (redirect); Mijn werk is dus de startplek — geen apart Home-item meer
      ...sectieItem('mijnwerk', 'Mijn Werk', WorkOutlineIcon, 'dagelijks', ['/notes', '/home']),
      ...sectieItem('planning', 'Planning', ViewWeekIcon, 'dagelijks'),
      { path: '/projects', label: 'Projecten', Icon: FolderOpenIcon, groep: 'dagelijks' },
      ...sectieItem('accounts', 'Accounts', BusinessIcon, 'organisatie'),
      ...sectieItem('productie', 'Productie', TheatersIcon, 'organisatie'),
      ...sectieItem('ai', 'AI', AutoFixHighIcon, 'organisatie'),
      ...sectieItem('operations', 'Operations', ShowChartIcon, 'sturing'),
    ],
    tools: [],
    toolsLabel: '',
    bottom: [
      ...sectieItem('lab', 'Lab', ScienceIcon, 'beheer'),
    ],
  };
}

/**
 * Staat een sectie in het hoofdmenu? Zodra de gebruiker minstens één scherm (niet de
 * hub) uit die sectie mag zien — op featurerecht, los van de BETA/PLANNED-schakelaars
 * (die verbergen items, geen hele secties). Een sectie met eigen `feature` (Lab) vraagt
 * dat recht ook. Zo ziet iemand met alleen Forecast of Klanttevredenheid Operations wél.
 */
export function sectieZichtbaar(key, permissions = {}) {
  const sectie = SECONDARY_NAV_SECTIONS[key];
  if (!sectie) return false;
  if (sectie.feature && !permissions[sectie.feature]?.view) return false;
  return sectie.items.some((i) => i.path !== sectie.hubPath && (!i.feature || permissions[i.feature]?.view));
}

/**
 * PLANNING = één scherm met weergaven (ronde 4, besluit Joris 25 sep). De
 * planningsschermen delen een tabbalk in de kopbalk (ui-components/PlanningTabs).
 * Routes blijven bestaan; het menu-item Planning heeft geen submenu meer. De overige
 * planningstools staan op de hub (/planning, "Alle planningstools") en in ⌘K.
 * 26 sep (gebruiksanalyse prod, besluit Joris): Draaidagen en Materialen zijn de langst
 * openstaande schermen en zaten drie klikken diep — nu vaste tabs, achter de vier
 * weergaven (eerst wie/wat/wanneer, dan de shoots en het materiaal ervoor). Oude
 * URL's (/draaidagen, /MaterialReservation) blijven gewoon werken.
 */
export const PLANNING_TABS = [
  { path: '/planner-todo', label: 'Rooster', feature: 'planner_todo' },
  { path: '/capacity', label: 'Capaciteit' },
  { path: '/planning/inplannen', label: 'Nog in te plannen', feature: 'planner_todo' },
  // Het "Excel" van de planning: alle taken per project en deliverable, kopiëren/plakken
  // en dupliceren (Joris, 26 sep: "hebben we op test nog zo'n excel?" — stond onder Meer).
  { path: '/planning/overzicht-per-project', label: 'Planningsgrid' },
  { path: '/dagstart', label: 'Dagstart' },
  { path: '/draaidagen', label: 'Draaidagen' },
  { path: '/MaterialReservation', label: 'Materialen' },
];

/** Tabs die deze gebruiker mag zien (featurerecht; zonder recht verdwijnt de tab). */
export function getPlanningTabs(permissions = {}) {
  return PLANNING_TABS.filter((t) => !t.feature || permissions[t.feature]?.view);
}

export function isPlanningTabPath(pathname) {
  return PLANNING_TABS.some((t) => t.path === pathname);
}

/** Stille kopjes boven de hoofdmenugroepen (dagelijks werk bovenaan heeft er geen). */
export const MENU_GROEPEN = {
  dagelijks: null,
  organisatie: 'Klanten & tools',
  sturing: 'Sturing',
};

/** Mag deze gebruiker dit sectie-item zien (featurerecht + BETA/PLANNED-schakelaars)? */
export function sectieItemToegestaan(item, permissions = {}, visibility = DEFAULT_ZICHTBAAR) {
  if (!item) return false;
  if (item.feature && !permissions[item.feature]?.view) return false;
  if (item.beta && !visibility.showBeta) return false;
  if (item.planned && !visibility.showPlanned) return false;
  return true;
}

/**
 * Startscherm van een sectie: waar een klik op het hoofditem heen gaat. Eerste
 * kandidaat uit `start` die de gebruiker mag zien; anders de hub.
 */
export function sectieStart(key, permissions = {}, visibility = DEFAULT_ZICHTBAAR) {
  const sectie = SECONDARY_NAV_SECTIONS[key];
  if (!sectie) return '/';
  const hit = (sectie.start || []).find((pad) =>
    sectieItemToegestaan(sectie.items.find((i) => i.path === pad), permissions, visibility));
  return hit || sectie.hubPath;
}

/**
 * Max. aantal kernschermen onder een uitgeklapt hoofditem (daarna één link "Alle …"
 * naar de hub). Ronde 3: 4 i.p.v. 6 — zeven gelijkwaardige subitems namen het midden
 * van het menu over.
 */
export const MAX_ACCORDEON = 4;

/**
 * Items voor de uitgeklapte sectie in het hoofdmenu. Neemt de eerste MAX_ACCORDEON
 * (al gefilterde) items zonder `meer`, in configvolgorde, zonder de hub zelf. Sta je
 * op een scherm dat daar niet bij zit, dan komt dat er (gemarkeerd) achteraan bij,
 * zodat je altijd ziet waar je bent. `verborgen` = hoeveel er alleen via de hub/⌘K gaan.
 * `vast` = de startschermen van de sectie (Mijn werk → Vandaag): die staan er altijd
 * vooraan bij en tellen niet mee voor de vier (26 sep, submenu op gebruik gesorteerd).
 */
export function accordeonItems(items = [], hubPath = null, isActief = () => false, vast = []) {
  const kandidaten = items.filter((i) => i.path !== hubPath);
  const vasteItems = kandidaten.filter((i) => vast.includes(i.path));
  const kort = [
    ...vasteItems,
    ...kandidaten.filter((i) => !i.meer && !vasteItems.includes(i)).slice(0, MAX_ACCORDEON),
  ];
  const actiefErbuiten = kandidaten.find((i) => !kort.includes(i) && isActief(i));
  return {
    items: actiefErbuiten ? [...kort, actiefErbuiten] : kort,
    verborgen: kandidaten.length - kort.length,
  };
}

/**
 * Kruimelpad voor de kopbalk: [sectie, scherm]. De sectie linkt naar haar hub.
 * Alleen waar het iets toevoegt: leeg op hubs, op de planningsweergaven (daar staat de
 * tabbalk) en op schermen die het menu zelf al als actief item toont (`zichtbaarInMenu`:
 * hoofditems + het uitgeklapte submenu) — anders zeg je twee keer hetzelfde.
 */
export function getKruimelpad(pathname, zichtbaarInMenu = []) {
  if (isHubPath(pathname) || isPlanningTabPath(pathname)) return [];
  if (zichtbaarInMenu.includes(pathname)) return [];
  // Projectdetail: de terugknop in de kop zegt al "Projecten" — "Projecten ›
  // Project Details" was dubbelop (planning-gauntlet, 25 sep 2026).
  if (/^\/projects\/[^/]+\/?$/.test(pathname)) return [];
  const opPad = (p) => pathname === p || pathname.startsWith(p + "/");
  const sectie = Object.values(SECONDARY_NAV_SECTIONS).find((s) =>
    s.paths.some(opPad) || s.items.some((i) => opPad(i.path)));
  const titel = getPageTitle(pathname);
  const kruimels = [];
  if (sectie) kruimels.push({ label: sectie.label, path: sectie.hubPath });
  else if (opPad("/projects")) kruimels.push({ label: "Projecten", path: "/projects" });
  if (!kruimels.length || kruimels[0].label !== titel) kruimels.push({ label: titel, path: null });
  return kruimels;
}

/** Is een hoofdmenu-item actief voor dit pad (eigen pad of een scherm uit zijn sectie)? */
export function isSidebarItemActive(item, pathname) {
  const paden = [item.path, ...(item.activePaths || [])];
  return paden.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

// -- Secondary navigation (section sub-menus) ---------------------------------
// Visible when browsing any child page within the section.

export const SECONDARY_NAV_SECTIONS = {
  mijnwerk: {
    label: "Mijn Werk",
    hubPath: "/mijn-werk",
    // Submenu (accordeon) alleen waar het startscherm geen tooloverzicht is: Vandaag
    // resp. de accountlijst. Secties die op hun hub starten tonen daar al alles.
    submenu: true,
    // Klik op "Mijn Werk" = je dag (Vandaag); het overzicht blijft via "Alle …"
    start: ["/vandaag"],
    // Altijd in de korte lijst, ongeacht gebruik (Joris 27 sep: "waar vind ik de overleggen?").
    vast: ["/overleg"],
    alleLabel: "Alles in Mijn werk",
    paths: ["/mijn-werk", "/vandaag", "/home", "/dashboard", "/mijn-todos", "/mijn-taken", "/slim-uren", "/mail-assistent", "/ai-mail", "/weekplan", "/MeetingDashboard", "/overdracht", "/leveranciersbeoordelingen", "/medewerkers", "/overleg", "/overleg/acties", "/verbeterregister", "/bestanden"],
    // Zichtbaar: max. 7 (dagritme). `meer: true` → onder "Meer" (ingeklapt). Groepen blijven voor de hub-lijst.
    // Volgorde van de groepen op de hub (items houden hun menuvolgorde)
    hubGroepen: ["Dag en week", "Taken en notities", "Mail, meetings en overleg", "Administratie"],
    items: [
      { path: "/mijn-werk", label: "Overzicht" },
      // Volgorde = menu-volgorde. Vandaag (startscherm) staat er altijd bovenaan bij; daarna
      // de vier meestgebruikte schermen (gebruiksanalyse prod, 30 dagen — besluit Joris
      // 26 sep): Meetings, Overdracht, Weekplan, Notities. De rest staat onder "Alles in
      // Mijn werk" (hub) en in ⌘K.
      { path: "/vandaag", label: "Vandaag", group: "Dag en week" },
      { path: "/MeetingDashboard", label: "Meetings", group: "Mail, meetings en overleg", ai: true },
      { path: "/overdracht", label: "Overdracht", group: "Administratie", beta: true, ai: true },
      { path: "/weekplan", label: "Weekplan", group: "Dag en week", beta: true },
      // /notes staat bewust niet in `paths`: de notitiepagina houdt haar eigen breedte
      { path: "/notes", label: "Notities", group: "Taken en notities", hub: { icon: "sticky_note_2", desc: "Supersnel een notitie vastleggen (iemand belt) — koppelen aan klant of project kan later" } },
      { path: "/mijn-taken", label: "Projecttaken", group: "Taken en notities", beta: true, meer: true },
      { path: "/mijn-todos", label: "Privé-to-do's", group: "Taken en notities", beta: true, meer: true },
      { path: "/slim-uren", label: "Slim Uren", group: "Administratie", beta: true, ai: true, meer: true },
      { path: "/overleg", label: "Overleggen", group: "Mail, meetings en overleg", feature: "overleg", beta: true, meer: true },
      { path: "/dashboard", label: "Mijn Dashboard", group: "Dag en week", meer: true },
      { path: "/home", label: "Mijn widgets", group: "Dag en week", meer: true, hub: { icon: "widgets", desc: "Je eigen widgetbord (was de startpagina) — indeling per gebruiker" } },
      // Weekoverzicht per PL hoort bij Planning (paths), maar PL's zoeken hem in hun eigen
      // werk: daarom ook hier, onder "meer" (hub Mijn werk).
      { path: "/planning/weekoverzicht-pl", label: "Weekoverzicht per PL", group: "Dag en week", beta: true, meer: true },
      { path: "/mail-assistent", label: "Frits (mail)", group: "Mail, meetings en overleg", beta: true, ai: true, meer: true },
      { path: "/ai-mail", label: "AI-mail", group: "Mail, meetings en overleg", beta: true, ai: true },
      { path: "/leveranciersbeoordelingen", label: "Leveranciersbeoordelingen", group: "Administratie", meer: true, hub: { icon: "star", desc: "Kwartaalbeoordeling van leveranciers en freelancers per projectleider" } },
      { path: "/overleg/acties", label: "Actieregister", group: "Mail, meetings en overleg", feature: "overleg", beta: true, meer: true },
      { path: "/medewerkers", label: "Medewerkers", group: "Administratie", beta: true, meer: true, hub: { icon: "groups", desc: "Wie werkt er bij Full Frame: foto, naam en functies" } },
      { path: "/verbeterregister", label: "Verbeterregister", group: "Mail, meetings en overleg", feature: "overleg", beta: true, meer: true, hub: { icon: "trending_up", desc: "Verbeterpunten uit projecten en overleggen: van bespreken tot geborgd" } },
      { path: "/bestanden", label: "Bestanden delen", group: "Mail, meetings en overleg", feature: "bestanden", beta: true, meer: true, hub: { icon: "send", desc: "Een bestand (pdf, rapport, render) intern naar collega's sturen; blijft 90 dagen staan" } },
    ],
  },
  planning: {
    label: "Planning",
    hubPath: "/planning",
    // Klik op "Planning" = het rooster zelf (wie doet wat wanneer — het dagelijkse
    // planscherm). Zonder planner-recht: Capaciteit. Geen submenu: de weergaven (incl.
    // Draaidagen en Materialen) staan als tabs bovenin (PLANNING_TABS); de hub via "Alle planningstools".
    start: ["/planner-todo", "/capacity"],
    alleLabel: "Alle planningstools",
    paths: ["/planning", "/planning/corvee", "/planning/voertuigen", "/planning/overzicht-per-project", "/planning/weekoverzicht-pl", "/planning/inplannen", "/dagstart", "/draaidagen", "/task-board", "/capacity", "/planner-todo", "/slimme-toewijzing", "/draaidag-planner", "/MaterialReservation", "/materiaal-beheer", "/materiaal-beheer/materialen", "/materiaal-beheer/sets", "/materiaal-beheer/reparatie"],
    items: [
      { path: "/planning", label: "Overzicht" },
      // De weergaven (tabs): Rooster, Capaciteit, Nog in te plannen, Planningsgrid, Dagstart, Draaidagen, Materialen
      { path: "/planner-todo", label: "Rooster", feature: "planner_todo", group: "Plannen", beta: true },
      { path: "/dagstart", label: "Dagstart", group: "Overzichten", beta: true },
      { path: "/capacity", label: "Capaciteit", group: "Overzichten", beta: true },
      { path: "/planning/inplannen", label: "Nog in te plannen", feature: "planner_todo", group: "Plannen", beta: true },
      { path: "/draaidagen", label: "Draaidagen", group: "Overzichten", beta: true },
      { path: "/MaterialReservation", label: "Materialen reserveren", group: "Materiaal en vervoer" },
      { path: "/task-board", label: "Weekbord", group: "Overzichten", beta: true },
      { path: "/planning/overzicht-per-project", label: "Planningsgrid per project", group: "Plannen", beta: true },
      { path: "/planning/weekoverzicht-pl", label: "Weekoverzicht per PL", group: "Overzichten", beta: true, meer: true },
      { path: "/slimme-toewijzing", label: "Slimme toewijzing", feature: "planner_todo", group: "Plannen", beta: true, meer: true },
      { path: "/draaidag-planner", label: "Draaidag-planner", feature: "planner_todo", group: "Plannen", beta: true, meer: true },
      { path: "/materiaal-beheer/materialen", label: "Materiaalbeheer", group: "Materiaal en vervoer", meer: true },
      { path: "/planning/voertuigen", label: "Voertuigen & Brandstof", group: "Materiaal en vervoer", meer: true },
      { path: "/planning/corvee", label: "Corvee", group: "Kantoor", beta: true, meer: true },
    ],
  },
  accounts: {
    label: "Accounts",
    hubPath: "/accounts",
    // De accountlijst is geen tooloverzicht: Klantaanvragen/Contentkalender/Marketing OS
    // (en in een workspace de tabbladen van het account) staan eronder in het menu
    submenu: true,
    // De accountlijst ís het dagelijkse scherm
    start: ["/accounts"],
    alleLabel: "Alle accounts",
    paths: ["/accounts", "/marketing", "/aanvragen-portaal", "/contentkalender"],
    items: [
      { path: "/accounts", label: "Overzicht" },
      { path: "/aanvragen-portaal", label: "Klantaanvragen", group: "Werkproces", beta: true },
      { path: "/contentkalender", label: "Contentkalender", group: "Werkproces", beta: true },
      { path: "/marketing", label: "Marketing OS", group: "Werkproces", beta: true },
    ],
  },
  productie: {
    label: "Productie",
    hubPath: "/productie",
    // Gereedschapskist zonder één dominant scherm → hub
    alleLabel: "Alle productietools",
    paths: ["/productie", "/callsheets", "/script-breakdown", "/productie-breakdown", "/ai-voortgang", "/video-qa", "/mxf-check", "/ondertiteling", "/SRTGenerator", "/ritmo", "/SRTDashboard", "/video-downloader", "/name-title-generator", "/formaat-omzetten", "/pptx-video-replacer", "/narrowcasting", "/pdf-combiner", "/video-search", "/explainer-script", "/scriptstijlen", "/animatie-leren", "/animatie-jobs", "/effie-guide", "/color-converter", "/qr-generator"],
    items: [
      { path: "/productie", label: "Overzicht" },
      // Hubgroepen = werkstap (hub-herziening 25 sep): voorbereiden → animatie → montage →
      // ondertiteling → na oplevering → hulpmiddelen. Volgorde hier = volgorde op de hub.
      // `hub.desc` wint van de ITEM_META in ProductieHub.jsx.
      { path: "/script-breakdown", label: "Script Breakdown", group: "Voorbereiden", beta: true, hub: { icon: "movie_filter", desc: "Script ontleden in scènes en shots voor de planning" } },
      { path: "/productie-breakdown", label: "Productie Breakdown", group: "Voorbereiden", beta: true, hub: { icon: "fact_check", desc: "Per productie bijhouden wat er geregeld moet worden: cast, locaties, props — met opties en status" } },
      { path: "/explainer-script", label: "Explainer Studio", group: "Voorbereiden", meer: true, hub: { icon: "auto_fix_high", desc: "Script voor een explainer schrijven, met hook, ritme en register" } },
      { path: "/scriptstijlen", label: "Scriptstijlen", group: "Voorbereiden", meer: true, hub: { icon: "edit_note", desc: "Per filmtype de schrijfstijl waar de scriptgenerator mee werkt" } },
      { path: "/callsheets", label: "Callsheets", group: "Voorbereiden", hub: { icon: "description", desc: "Callsheet per draaidag: locatie, tijden en crew" } }, // FU-07: actieve tool — geen planned/beta-badge, anders standaard onzichtbaar
      { path: "/animatie-jobs", label: "Animatie-jobs", group: "Animatie", beta: true, meer: true, hub: { icon: "animation", desc: "Storyboard en voice-over aanleveren; Astra bouwt de animatie in After Effects" } },
      { path: "/ai-voortgang", label: "AI Voortgang", feature: "ai_voortgang", group: "Animatie", beta: true, hub: { icon: "task_alt", desc: "Per fase akkoord van de klant vragen bij AI-video's: versies delen, akkoord of wijziging via het klantportaal" } },
      { path: "/animatie-leren", label: "Animatie leren", group: "Animatie", beta: true, meer: true, hub: { icon: "school", desc: "Oude After Effects- en Illustrator-bestanden per klant uploaden als voorbeeld voor Astra" } },
      { path: "/video-qa", label: "Edit QA", group: "Montage & afwerking", beta: true, hub: { icon: "fact_check", desc: "Een edit automatisch laten controleren op editfouten en verwerkte feedback" } },
      { path: "/mxf-check", label: "MXF Check", feature: "mxf_check", group: "Montage & afwerking", beta: true, hub: { icon: "live_tv", desc: "TV-aanlevering (MXF) toetsen tegen de Screenforce-specs: beeld, audiokanalen en loudness" } },
      { path: "/name-title-generator", label: "Titels", group: "Montage & afwerking", beta: true, hub: { icon: "badge", desc: "Naamtitels renderen vanuit huisstijltemplates" } },
      { path: "/formaat-omzetten", label: "Formaat omzetten", group: "Montage & afwerking", beta: true, externalUrl: FORMAAT_OMZETTEN_URL, hub: { icon: "aspect_ratio", desc: "Video omzetten naar 9:16, 4:5 of 1:1 met slim kader (in Flow, Full Frame-account)" } },
      { path: "/ondertiteling", label: "Ondertiteling", group: "Ondertiteling", hub: { icon: "subtitles", desc: "SRT-bestanden: transcriberen, vertalen en exporteren als los bestand" } }, // uit beta (Joris, 6 sep)
      { path: "/ritmo", label: "Ondertitel Studio", group: "Ondertiteling", hub: { icon: "movie_edit", desc: "Ondertitels ín beeld: stijl kiezen, woord voor woord timen, renderen als MP4 of ProRes" } }, // uit beta (Joris, 6 sep)
      { path: "/video-search", label: "Videotheek", group: "Na oplevering", beta: true, hub: { icon: "video_library", desc: "Opgeleverde video's en inspiratie doorzoeken op tekst, beeld, klant of projectnummer" } },
      { path: "/narrowcasting", label: "Narrowcasting", group: "Na oplevering", beta: true, meer: true, hub: { icon: "tv", desc: "Content voor narrowcasting-schermen" } },
      { path: "/effie-guide", label: "Effie Gids", group: "Na oplevering", meer: true, hub: { icon: "emoji_events", desc: "Hulp bij het schrijven van een Effie Award-inzending" } },
      { path: "/video-downloader", label: "Downloader", group: "Hulpmiddelen", tier: "utility", beta: true, meer: true },
      { path: "/pptx-video-replacer", label: "PPTX Replacer", group: "Hulpmiddelen", tier: "utility", beta: true, meer: true },
      { path: "/pdf-combiner", label: "PDF Combiner", group: "Hulpmiddelen", tier: "utility", meer: true },
      { path: "/color-converter", label: "Kleur Converter", group: "Hulpmiddelen", tier: "utility", meer: true },
      { path: "/qr-generator", label: "QR-generator", group: "Hulpmiddelen", tier: "utility", meer: true },
    ],
  },
  ai: {
    label: "AI",
    hubPath: "/ai-hub",
    // AI chat is een full-screen editor (zonder menu) → geen goed startscherm; hub
    alleLabel: "Alle AI-tools",
    paths: ["/ai-hub", "/ai-chat", "/gpt-maker", "/inspiration", "/avatar-chat", "/ai-beelden"],
    items: [
      { path: "/ai-hub", label: "Overzicht" },
      { path: "/ai-chat", label: "AI chat project", group: "Chat en assistenten" },
      { path: "/gpt-maker", label: "GPT Bouwer", group: "Chat en assistenten" },
      { path: "/inspiration", label: "Inspiratie", group: "Beeld en inspiratie", ai: true },
      { path: "/avatar-chat", label: "Avatar Chat", group: "Chat en assistenten", ai: true },
      { path: "/ai-beelden", label: "AI-beelden", group: "Beeld en inspiratie", ai: true, beta: true },
    ],
  },
  operations: {
    label: "Operations",
    hubPath: "/operations",
    // Dashboards per rol verschillend (featurerechten) → hub als start
    alleLabel: "Alle Operations",
    paths: ["/operations", "/joris-dashboard", "/strategy", "/sales-dashboard", "/sales/prospects", "/offertes", "/achmea-export", "/revenue-forecast", "/budget", "/opdracht-mix", "/ohw-check", "/uren-check", "/bbi-doel", "/enquete-dashboard", "/declarabiliteit", "/hr-dashboard", "/leveranciers", "/sleutels", "/werving"],
    items: [
      { path: "/operations", label: "Overzicht" },
      { path: "/joris-dashboard", label: "Werkdruk PL's & team", feature: "kpi_dashboard", group: "Sturing" },
      { path: "/declarabiliteit", label: "Declarabiliteit", feature: "kpi_dashboard", group: "Sturing", beta: true },
      { path: "/sales-dashboard", label: "Sales", feature: "sales_dashboard", group: "Commercieel" },
      { path: "/offertes", label: "Offerte Generator", feature: "offertes", group: "Commercieel" },
      { path: "/revenue-forecast", label: "Forecast", feature: "revenue_forecast", group: "Commercieel" },
      { path: "/budget", label: "Budget", feature: "budget", group: "Commercieel", beta: true, hub: { icon: "savings", desc: "Omzet per account, target volgend jaar, gewogen pijplijn en export naar de Excel" } },
      { path: "/ohw-check", label: "OHW", feature: "kpi_dashboard", group: "Financieel" },
      { path: "/uren-check", label: "Uren", feature: "kpi_dashboard", group: "Financieel" },
      { path: "/opdracht-mix", label: "Opdracht-mix", feature: "kpi_dashboard", group: "Sturing", beta: true, meer: true },
      { path: "/sales/prospects", label: "Prospects", feature: "sales_prospects", group: "Commercieel", meer: true },
      { path: "/achmea-export", label: "Achmea factuur-export", feature: "offertes", group: "Financieel", beta: true, meer: true, hub: { icon: "receipt_long", desc: "Gripp-factuur omzetten naar Achmea-tariefcodes en als Excel downloaden" } },
      { path: "/bbi-doel", label: "BBI-doel", feature: "kpi_dashboard", group: "Financieel", beta: true, meer: true },
      { path: "/strategy", label: "Strategie", feature: "strategy", group: "Sturing", meer: true },
      { path: "/enquete-dashboard", label: "Klanttevredenheid", feature: "enquete_dashboard", group: "Sturing", meer: true },
      { path: "/leveranciers", label: "Leveranciers", feature: "leveranciers", group: "Mensen", beta: true, hub: { icon: "handshake", desc: "Freelancers en bedrijven: contact, tarieven, beoordelingen en inzet op projecten" } },
      { path: "/hr-dashboard", label: "HR-dashboard", feature: "hr_leveranciers", group: "Mensen", beta: true, meer: true, hub: { icon: "diversity_3", desc: "Samenstelling van het team in aantallen — geen gegevens per persoon" } },
      { path: "/sleutels", label: "Sleutels", feature: "sleutels", group: "Kantoor", beta: true, meer: true, hub: { icon: "vpn_key", desc: "Keytags van het pand: wie heeft welke tag, profiel en toegang" } },
      { path: "/werving", label: "Werving", feature: "werving", group: "Mensen", beta: true, hub: { icon: "person_search", desc: "Vacatures, sollicitaties uit ikwilwerken@ en de scorecard van het MT" } },
    ],
  },
  lab: {
    label: "Lab",
    hubPath: "/lab",
    feature: "lab",
    alleLabel: "Alle experimenten",
    paths: ["/lab", "/lab/ahlstrom-interactive", "/website-preview"],
    items: [
      { path: "/lab", label: "Overzicht" },
      { path: "/lab/ahlstrom-interactive", label: "Ahlstrom Interactive", group: "Experimenten", beta: true },
      { path: "/website-preview", label: "Website Preview", feature: "website_feedback", group: "Experimenten", beta: true },
    ],
  },
};

// -- Layout metadata (single source of truth voor App.js + AppShell) ----------
// Twee aparte concepten die eerder als losse hardcoded lijsten uiteenliepen:
//  1. Full-bleed: content krijgt géén gecentreerde grid-wrapper (App.js).
//  2. Full-screen: echte editors — verbergen óók hoofd-sidebar + secundaire nav.

const FULL_BLEED_PATHS = [
  "/", "/home", "/beta", "/vandaag", "/ai-chat", "/gpt-maker", "/accounts",
  "/strategy", "/sales/prospects", "/MaterialReservation",
  "/name-title-generator", "/mijn-profiel", "/offertes",
  "/color-converter", "/qr-generator", "/planning/voertuigen",
  // Planner To-Do: resource-rooster, elke pixel breedte is een dagkolom (26 sep)
  "/planner-todo",
  // Dagstart: het team-rooster beeldvullend, zonder 1800-cap (Joris 7 okt)
  "/dagstart",
];
const FULL_BLEED_PREFIXES = ["/accounts/", "/materiaal-beheer", "/planning/corvee", "/MeetingDashboard/", "/overleg/sessie/"];

/** Content zonder gecentreerde wrapper (full-bleed pagina's). */
export function isFullBleedPath(pathname) {
  return (
    FULL_BLEED_PATHS.includes(pathname) ||
    FULL_BLEED_PREFIXES.some((p) => pathname.startsWith(p))
  );
}

/** Echte full-screen editors: verbergen zowel hoofd-sidebar als secundaire nav. */
// Dagstart (Joris 7 okt): "beeldvullend, het linkermenu moet weg" — de ingeklapte focusstrook
// en een vastgezet planningsmenu kostten nog steeds dagkolommen. Menu via de hamburger.
const FULL_SCREEN_PATHS = ["/ai-chat", "/gpt-maker", "/dagstart"];
export function isFullScreenPath(pathname) {
  return FULL_SCREEN_PATHS.includes(pathname);
}

/**
 * FOCUSSCHERMEN (menu-herziening 25 sep): het hoofdmenu klapt hier vanzelf in tot een
 * smalle strook met een »-knop (hover schuift het er tijdelijk overheen), en het
 * sectiemenu verdwijnt. Wie het menu op zo'n scherm vastzet, krijgt dat per scherm
 * onthouden (SidebarNav, localStorage). Mobiel: ongewijzigd (hamburger).
 * Ronde 4: de vier planningsweergaven zijn samen één focusscherm met één sleutel
 * ("planning") — een tab wisselen laat het menu niet in- of uitklappen, en wie het menu
 * vastzet heeft het op alle vier. Alle vier zijn werkvlakken waar breedte telt.
 * 26 sep: Draaidagen en Materialen zijn ook tabs en delen dus dezelfde sleutel.
 */
const FOCUS_PATHS = ["/vandaag"];
const FOCUS_PREFIXES = ["/overleg/sessie/"];
export const PLANNING_FOCUS_KEY = "planning";

/** Sleutel van het focusscherm voor dit pad (om de menukeuze per scherm te onthouden), of null. */
export function getFocusKey(pathname) {
  if (isPlanningTabPath(pathname)) return PLANNING_FOCUS_KEY;
  if (FOCUS_PATHS.includes(pathname)) return pathname;
  const prefix = FOCUS_PREFIXES.find((p) => pathname.startsWith(p));
  return prefix ? prefix.replace(/\/$/, "") : null;
}

export function isFocusPath(pathname) {
  return getFocusKey(pathname) !== null;
}

/**
 * DESIGN-PRINCIPE: werk-/detailpagina's tonen géén secundaire sub-nav — die
 * ruimte gaat naar het werk zelf, of naar een tool-eigen linkerkolom (offertes:
 * gesprekken, meetings: je meeting-lijst). De hoofd-sidebar blijft altijd staan.
 * Zie policies/ff-design.md.
 */
export function hidesSecondaryNav(pathname) {
  return (
    isFullScreenPath(pathname) ||
    // Focusschermen: menu weg, alleen het werk (Vandaag, Planner To-Do, Dagstart, overleg-sessie)
    isFocusPath(pathname) ||
    /^\/callsheets(\/.+)?$/.test(pathname) ||
    // Productie Breakdown heeft een eigen producties-kolom op die plek
    /^\/productie-breakdown(\/.+)?$/.test(pathname) ||
    // AI Voortgang heeft een eigen projecten-kolom op die plek
    /^\/ai-voortgang(\/.+)?$/.test(pathname) ||
    pathname === "/offertes" ||
    // Mijn To-Do's heeft een eigen lijsten-kolom op die plek (2 sep)
    pathname === "/mijn-todos" ||
    // Planner To-Do is een werk-rooster; de sub-nav kost dagkolommen (26 sep)
    pathname === "/planner-todo" ||
    // Vandaag is een dagplan (lijst + tijdlijn); de sub-nav kost de dag ruimte (26 sep)
    pathname === "/vandaag" ||
    pathname.startsWith("/MeetingDashboard/") ||
    // Overlegpagina heeft een eigen sessies-kolom op die plek
    pathname.startsWith("/overleg/sessie/") ||
    // Sollicitantkaart heeft een eigen sollicitanten-kolom op die plek (werving sprint 1)
    pathname.startsWith("/werving/sollicitatie/")
  );
}

/** Hub-/landingspagina's: topbar-titel verbergen — body header is leidend. */
export function isHubPath(pathname) {
  if (pathname === "/" || pathname === "/home" || pathname === "/projects") return true;
  return Object.values(SECONDARY_NAV_SECTIONS).some((s) => s.hubPath === pathname);
}

/**
 * Get secondary nav for a pathname.
 * Shows on hub overview pages and their direct child routes.
 * Collapses on tool pages so content gets full width.
 */
export function getSecondaryNav(pathname, permissions = {}) {
  const section = Object.values(SECONDARY_NAV_SECTIONS).find((candidate) =>
    candidate.paths.some((p) => pathname === p || pathname.startsWith(p + "/"))
  );

  if (!section) return null;

  return {
    label: section.label,
    items: section.items.filter((item) => !item.feature || permissions[item.feature]?.view),
  };
}

/**
 * Check if secondary nav should be shown as collapsed (icon-only) on this page.
 * Hub overviews show the full nav; tool pages collapse it to a thin strip.
 */
export function isSecondaryNavCollapsed(pathname) {
  // Never auto-collapse — user controls collapse state manually
  return false;
}
